import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  UserCheck, 
  ShieldAlert, 
  ArrowLeftRight, 
  Check, 
  AlertCircle,
  RefreshCw,
  Search,
  RotateCcw,
  Save
} from 'lucide-react';

export default function PricingConfig() {
  const [configs, setConfigs] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('GRID');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [gridValues, setGridValues] = useState({});
  const [gridSearch, setGridSearch] = useState('');
  
  // Pending changes state
  const [editableValues, setEditableValues] = useState({});
  const [savingKeys, setSavingKeys] = useState({});

  // Confirmation Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    key: null,
    value: null,
    label: null
  });

  const categories = [
    { id: 'GRID', label: 'Grille Kilométrique (1 à 100 km)', icon: ArrowLeftRight },
    { id: 'PRICING', label: 'Tarification Course', icon: DollarSign },
    { id: 'DRIVER_SUBSCRIPTION', label: 'Abonnements Chauffeurs', icon: UserCheck }
  ];

  const fetchConfigsAndHistory = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [configsRes, historyRes] = await Promise.all([
        fetch('/api/admin/configurations', { headers }),
        fetch('/api/admin/configurations/history', { headers })
      ]);

      if (!configsRes.ok || !historyRes.ok) {
        throw new Error('Erreur lors du chargement des données de configuration');
      }

      const configsData = await configsRes.json();
      const historyData = await historyRes.json();

      setConfigs(configsData);
      setHistory(historyData);

      // Prepopulate editable values
      const initialValues = {};
      configsData.forEach(c => {
        initialValues[c.settingKey] = c.settingValue;
      });
      setEditableValues(initialValues);

      // Parse grid pricing
      const gridConfig = configsData.find(c => c.settingKey === 'distance_pricing_grid');
      if (gridConfig) {
        try {
          const parsed = JSON.parse(gridConfig.settingValue);
          if (Array.isArray(parsed)) {
            const mapObj = {};
            parsed.forEach((val, idx) => {
              mapObj[idx + 1] = val;
            });
            setGridValues(mapObj);
          } else {
            setGridValues(parsed);
          }
        } catch (e) {
          console.error("Error parsing distance_pricing_grid:", e);
        }
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigsAndHistory();
  }, []);

  const handleInputChange = (key, value) => {
    setEditableValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const handleSaveClick = (config) => {
    const newValue = editableValues[config.settingKey];
    
    // Check if it's a major/core calculation change
    const majorKeys = ['minimum_price'];
    if (majorKeys.includes(config.settingKey)) {
      setConfirmModal({
        isOpen: true,
        key: config.settingKey,
        value: newValue,
        label: formatKeyLabel(config.settingKey)
      });
    } else {
      executeSave(config.settingKey, newValue);
    }
  };

  const executeSave = async (key, value) => {
    setSavingKeys(prev => ({ ...prev, [key]: true }));
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/configurations/${key}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value })
      });

      if (!res.ok) {
        throw new Error('Échec de la mise à jour de la configuration');
      }

      showSuccess(`Le paramètre ${formatKeyLabel(key)} a été mis à jour avec succès.`);
      await fetchConfigsAndHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKeys(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleRestoreClick = async (historyId, key) => {
    if (!window.confirm(`Voulez-vous vraiment restaurer l'ancienne valeur pour ${formatKeyLabel(key)} ?`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/configurations/history/${historyId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error('Échec de la restauration de la configuration');
      }

      showSuccess(`La valeur précédente pour ${formatKeyLabel(key)} a été restaurée.`);
      await fetchConfigsAndHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatKeyLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const parseGridArrayOrMap = (rawJson) => {
    if (!rawJson) return {};
    try {
      const parsed = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
      if (Array.isArray(parsed)) {
        return parsed.reduce((acc, val, idx) => ({ ...acc, [idx + 1]: val }), {});
      }
      return parsed || {};
    } catch (e) {
      return {};
    }
  };

  const getGridDifferences = (oldJson, newJson) => {
    const oldMap = parseGridArrayOrMap(oldJson);
    const newMap = parseGridArrayOrMap(newJson);
    const diffs = [];

    const allKeys = new Set([...Object.keys(oldMap), ...Object.keys(newMap)]);
    const sortedKeys = Array.from(allKeys).map(Number).sort((a, b) => a - b);

    for (const km of sortedKeys) {
      if (isNaN(km) || km <= 0) continue;
      const oldVal = oldMap[km] !== undefined ? parseFloat(oldMap[km]) : null;
      const newVal = newMap[km] !== undefined ? parseFloat(newMap[km]) : null;

      if (oldVal !== newVal) {
        diffs.push({
          km,
          oldVal: oldVal !== null ? `${oldVal.toFixed(2)} €` : 'N/A',
          newVal: newVal !== null ? `${newVal.toFixed(2)} €` : 'N/A',
        });
      }
    }
    return diffs;
  };

  const renderAuditSettingKey = (log) => {
    if (log.settingKey === 'distance_pricing_grid') {
      const diffs = getGridDifferences(log.oldValue, log.newValue);
      return (
        <div>
          <div style={{ fontWeight: 'bold', color: '#ffffff' }}>Grille Kilométrique</div>
          <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '2px' }}>
            {diffs.length} palier{diffs.length > 1 ? 's' : ''} modifié{diffs.length > 1 ? 's' : ''}
          </div>
        </div>
      );
    }
    return <span style={{ fontWeight: 'bold' }}>{formatKeyLabel(log.settingKey)}</span>;
  };

  const renderAuditOldValue = (log) => {
    if (log.settingKey === 'distance_pricing_grid') {
      const diffs = getGridDifferences(log.oldValue, log.newValue);
      if (diffs.length === 0) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {diffs.map(d => (
            <div key={d.km} style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#94a3b8', fontWeight: '600' }}>{d.km} km :</span>
              <span style={{ color: '#f87171', textDecoration: 'line-through', fontFamily: 'monospace' }}>{d.oldVal}</span>
            </div>
          ))}
        </div>
      );
    }
    const unit = log.settingKey.includes('percent') ? '%' : (log.settingKey.includes('price') || log.settingKey.includes('fee') ? ' €' : '');
    return (
      <span style={{ color: '#f87171', textDecoration: 'line-through', fontFamily: 'monospace' }}>
        {log.oldValue ?? 'N/A'}{unit}
      </span>
    );
  };

  const renderAuditNewValue = (log) => {
    if (log.settingKey === 'distance_pricing_grid') {
      const diffs = getGridDifferences(log.oldValue, log.newValue);
      if (diffs.length === 0) return <span style={{ color: 'var(--text-muted)' }}>Aucun changement</span>;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {diffs.map(d => (
            <div key={d.km} style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#94a3b8', fontWeight: '600' }}>{d.km} km :</span>
              <span style={{ color: '#10b981', fontWeight: '700', fontFamily: 'monospace' }}>{d.newVal}</span>
            </div>
          ))}
        </div>
      );
    }
    const unit = log.settingKey.includes('percent') ? '%' : (log.settingKey.includes('price') || log.settingKey.includes('fee') ? ' €' : '');
    return (
      <span style={{ color: '#10b981', fontWeight: '700', fontFamily: 'monospace' }}>
        {log.newValue}{unit}
      </span>
    );
  };

  const handleGridValueChange = (k, value) => {
    setGridValues(prev => ({
      ...prev,
      [k]: value === '' ? '' : parseFloat(value) || 0
    }));
  };

  const getOriginalGridValues = () => {
    const gridConfig = configs.find(c => c.settingKey === 'distance_pricing_grid');
    if (!gridConfig) return {};
    return parseGridArrayOrMap(gridConfig.settingValue);
  };

  const isKmModified = (km) => {
    const original = getOriginalGridValues();
    if (original[km] === undefined) return false;
    return parseFloat(original[km]) !== parseFloat(gridValues[km]);
  };

  const getModifiedCount = () => {
    const original = getOriginalGridValues();
    let count = 0;
    for (let i = 1; i <= 100; i++) {
      if (original[i] !== undefined && parseFloat(original[i]) !== parseFloat(gridValues[i])) {
        count++;
      }
    }
    return count;
  };

  const isGridChanged = () => {
    return getModifiedCount() > 0;
  };

  const handleResetGrid = () => {
    const original = getOriginalGridValues();
    setGridValues({ ...original });
  };

  const handleSaveGrid = async () => {
    const cleanGrid = [];
    for (let i = 1; i <= 100; i++) {
      const val = gridValues[i] !== undefined ? gridValues[i] : 9.90;
      cleanGrid.push(parseFloat(val) || 9.90);
    }
    const newJson = JSON.stringify(cleanGrid);
    await executeSave('distance_pricing_grid', newJson);
  };

  const renderGridEditor = () => {
    const isSaving = savingKeys['distance_pricing_grid'];
    const changed = isGridChanged();
    const modifiedCount = getModifiedCount();

    const allItems = [];
    for (let i = 1; i <= 100; i++) {
      allItems.push(i);
    }

    const filteredItems = allItems.filter(km => {
      if (!gridSearch) return true;
      const term = gridSearch.trim().toLowerCase();
      if (term === 'modifie' || term === 'modifié' || term === 'modifiés' || term === 'changes') {
        return isKmModified(km);
      }
      return String(km).includes(term) || `${km}km`.includes(term) || `${km} km`.includes(term);
    });

    return (
      <div style={styles.gridContainer}>
        {/* Sticky Grid Control Header */}
        <div style={styles.gridEditorHeader} className="pricing-grid-header">
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h3 style={styles.gridEditorTitle}>Grille Tarifaire Forfaitaire (1 à 100 km)</h3>
              {changed && (
                <span style={styles.modifiedBadgeTop}>
                  ● {modifiedCount} modif{modifiedCount > 1 ? 's' : ''} en attente
                </span>
              )}
            </div>
            <p style={styles.gridEditorDesc}>
              Modifiez directement le prix forfaitaire en euros (€) pour chaque palier kilométrique.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }} className="pricing-header-actions">
            {changed && (
              <button
                type="button"
                onClick={handleResetGrid}
                disabled={isSaving}
                style={styles.resetBtn}
                title="Rétablir les valeurs actuelles"
              >
                <RotateCcw size={16} />
                <span>Rétablir</span>
              </button>
            )}

            <button
              type="button"
              disabled={!changed || isSaving}
              onClick={handleSaveGrid}
              style={{
                ...styles.mainSaveBtn,
                backgroundColor: changed ? '#10b981' : 'var(--bg-tertiary)',
                color: changed ? '#ffffff' : 'var(--text-muted)',
                boxShadow: changed ? '0 4px 14px 0 rgba(16, 185, 129, 0.4)' : 'none',
                cursor: changed ? 'pointer' : 'not-allowed',
              }}
            >
              {isSaving ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              <span>
                {changed
                  ? `Enregistrer (${modifiedCount})`
                  : 'Grille à jour'}
              </span>
            </button>
          </div>
        </div>

        {/* Filter / Search Bar */}
        <div style={styles.searchBarContainer} className="pricing-search-container">
          <div style={styles.searchInputWrapper} className="pricing-search-input-wrapper">
            <Search size={16} color="var(--text-muted)" style={{ marginLeft: '12px', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Rechercher un km (ex: 15) ou 'modifié'..."
              value={gridSearch}
              onChange={(e) => setGridSearch(e.target.value)}
              style={styles.gridSearchInput}
            />
            {gridSearch && (
              <button 
                onClick={() => setGridSearch('')} 
                style={styles.clearSearchBtn}
              >
                ✕
              </button>
            )}
          </div>

          <div style={styles.quickFilters} className="pricing-quick-filters">
            <button 
              type="button"
              onClick={() => setGridSearch('')} 
              style={{ ...styles.filterChip, ...(gridSearch === '' ? styles.filterChipActive : {}) }}
            >
              Tous (1-100)
            </button>
            <button 
              type="button"
              onClick={() => setGridSearch('1')} 
              style={styles.filterChip}
            >
              1-19 km
            </button>
            <button 
              type="button"
              onClick={() => setGridSearch('modifié')} 
              style={{ 
                ...styles.filterChip, 
                ...(gridSearch.toLowerCase().includes('modifi') ? styles.filterChipActive : {}),
                color: changed ? '#10b981' : 'var(--text-muted)'
              }}
            >
              Modifiés ({modifiedCount})
            </button>
          </div>
        </div>

        {/* Grid Inputs Area */}
        {filteredItems.length === 0 ? (
          <div style={styles.emptyGridSearch}>
            Aucun kilomètre ne correspond à la recherche "{gridSearch}".
          </div>
        ) : (
          <div style={styles.gridInputsArea} className="pricing-inputs-grid">
            {filteredItems.map(km => {
              const isModified = isKmModified(km);
              return (
                <div 
                  key={km} 
                  className="pricing-input-cell"
                  style={{
                    ...styles.gridInputRow,
                    ...(isModified ? styles.gridInputRowModified : {})
                  }}
                >
                  <div style={styles.kmLabelContainer}>
                    <span 
                      className="pricing-km-text"
                      style={{
                        ...styles.gridKmLabel,
                        color: isModified ? '#10b981' : 'var(--text-secondary)'
                      }}
                    >
                      {km} km
                    </span>
                    {isModified && <span style={styles.modifiedIndicator}>●</span>}
                  </div>
                  <div style={styles.inputEuroWrapper}>
                    <input
                      type="number"
                      step="0.01"
                      className="pricing-km-input"
                      value={gridValues[km] ?? ''}
                      onChange={(e) => handleGridValueChange(km, e.target.value)}
                      style={{
                        ...styles.gridInput,
                        color: isModified ? '#10b981' : 'var(--text-primary)',
                        fontWeight: isModified ? '700' : '500'
                      }}
                    />
                    <span style={styles.gridEuroSymbol}>€</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Floating Bottom Action Bar when there are unsaved changes */}
        {changed && (
          <div style={styles.floatingActionBar} className="pricing-floating-bar">
            <div style={styles.floatingActionContent} className="pricing-floating-content">
              <div style={styles.floatingActionLeft}>
                <div style={styles.pulseDot} />
                <span style={styles.floatingText}>
                  <strong>{modifiedCount} modif{modifiedCount > 1 ? 's' : ''}</strong> en attente
                </span>
              </div>
              <div style={styles.floatingActionRight} className="pricing-floating-actions">
                <button
                  type="button"
                  onClick={handleResetGrid}
                  disabled={isSaving}
                  style={styles.floatingCancelBtn}
                >
                  <RotateCcw size={16} />
                  <span>Annuler</span>
                </button>
                <button
                  type="button"
                  onClick={handleSaveGrid}
                  disabled={isSaving}
                  style={styles.floatingSaveBtn}
                >
                  {isSaving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
                  <span>Enregistrer ({modifiedCount})</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderConfigInput = (config) => {
    const currentValue = editableValues[config.settingKey] ?? '';
    const hasChanged = currentValue !== config.settingValue;

    if (config.valueType === 'BOOLEAN') {
      return (
        <div style={styles.inputContainer}>
          <select 
            value={currentValue}
            onChange={(e) => handleInputChange(config.settingKey, e.target.value)}
            style={styles.select}
          >
            <option value="true">Actif (True)</option>
            <option value="false">Inactif (False)</option>
          </select>
        </div>
      );
    }

    return (
      <div style={styles.inputContainer}>
        <input 
          type="number"
          step="any"
          value={currentValue}
          onChange={(e) => handleInputChange(config.settingKey, e.target.value)}
          style={{
            ...styles.input,
            borderColor: hasChanged ? 'var(--blue-primary)' : 'var(--border-color)'
          }}
        />
        <span style={styles.unitSuffix}>
          {config.settingKey.includes('percent') ? '%' : (config.settingKey.includes('price') || config.settingKey.includes('fee') ? '€' : '')}
        </span>
      </div>
    );
  };

  if (loading && configs.length === 0) {
    return <div style={styles.loadingContainer}>Chargement de la configuration tarifaire...</div>;
  }

  const filteredConfigs = configs.filter(c => c.category === activeTab && c.settingKey !== 'distance_pricing_grid');

  return (
    <div style={styles.container} className="pricing-config-container">
      <style>{`
        .pricing-config-container {
          padding-bottom: 120px;
        }
        @media (max-width: 768px) {
          .pricing-config-container {
            padding: 10px 6px 140px 6px !important;
          }
          .pricing-grid-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
            padding: 12px 14px !important;
            top: 0 !important;
          }
          .pricing-header-actions {
            width: 100% !important;
          }
          .pricing-header-actions button {
            flex: 1 !important;
            justify-content: center !important;
          }
          .pricing-search-container {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
            padding: 10px !important;
          }
          .pricing-search-input-wrapper {
            min-width: 0 !important;
            width: 100% !important;
          }
          .pricing-quick-filters {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            padding-bottom: 4px !important;
            width: 100% !important;
            display: flex !important;
            gap: 6px !important;
          }
          .pricing-quick-filters button {
            flex-shrink: 0 !important;
          }
          .pricing-inputs-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 8px !important;
            padding: 10px 6px !important;
          }
          .pricing-floating-bar {
            left: 8px !important;
            right: 8px !important;
            bottom: 8px !important;
            width: auto !important;
            transform: none !important;
          }
          .pricing-floating-content {
            flex-direction: column !important;
            gap: 8px !important;
            padding: 10px 12px !important;
          }
          .pricing-floating-actions {
            width: 100% !important;
            display: flex !important;
            gap: 8px !important;
          }
          .pricing-floating-actions button {
            flex: 1 !important;
            justify-content: center !important;
            padding: 8px 12px !important;
            font-size: 0.85rem !important;
          }
          .pricing-cards-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          .pricing-tab-container {
            gap: 6px !important;
          }
          .pricing-tab-btn {
            padding: 8px 12px !important;
            font-size: 0.8rem !important;
          }
        }
        @media (max-width: 480px) {
          .pricing-inputs-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 6px !important;
            padding: 8px 4px !important;
          }
          .pricing-input-cell {
            padding: 6px 6px !important;
          }
          .pricing-km-text {
            width: 36px !important;
            font-size: 0.78rem !important;
          }
          .pricing-km-input {
            font-size: 0.85rem !important;
          }
        }
      `}</style>
      {/* Alert Banners */}
      {error && (
        <div style={styles.errorBanner}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div style={styles.successBanner}>
          <Check size={20} />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabContainer} className="pricing-tab-container">
        {categories.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button 
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="pricing-tab-btn"
              style={{
                ...styles.tabButton,
                ...(isActive ? styles.tabButtonActive : {})
              }}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Main Content */}
      {activeTab === 'GRID' ? (
        renderGridEditor()
      ) : (
        <div style={styles.grid} className="pricing-cards-grid">
          {filteredConfigs.map(config => {
            const isSaving = savingKeys[config.settingKey];
            const hasChanged = editableValues[config.settingKey] !== config.settingValue;

            return (
              <div key={config.settingKey} style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>{formatKeyLabel(config.settingKey)}</h3>
                  <span style={styles.cardKey}>{config.settingKey}</span>
                </div>
                
                <p style={styles.cardDesc}>{config.description}</p>
                
                <div style={styles.cardActionArea}>
                  {renderConfigInput(config)}
                  
                  <button
                    disabled={!hasChanged || isSaving}
                    onClick={() => handleSaveClick(config)}
                    style={{
                      ...styles.saveBtn,
                      opacity: hasChanged ? 1 : 0.5,
                      cursor: hasChanged ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                    <span>Enregistrer</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Audit History Logs */}
      <div style={styles.historySection} className="pricing-audit-section">
        <div style={styles.historyHeader}>
          <ArrowLeftRight size={18} color="var(--blue-primary)" />
          <h2 style={styles.historyTitle}>Historique d'Audit des Tarifs</h2>
        </div>
        
        <div style={styles.tablePanel} className="pricing-audit-table-wrap">
          <table style={styles.table} className="pricing-audit-table">
            <thead>
              <tr style={styles.tr}>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Paramètre</th>
                <th style={styles.th}>Ancienne Valeur</th>
                <th style={styles.th}>Nouvelle Valeur</th>
                <th style={styles.th}>Auteur</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const validHistory = history.filter(log => {
                  if (log.oldValue === log.newValue) return false;
                  if (log.settingKey === 'distance_pricing_grid') {
                    const diffs = getGridDifferences(log.oldValue, log.newValue);
                    return diffs.length > 0;
                  }
                  return true;
                });

                if (validHistory.length === 0) {
                  return (
                    <tr>
                      <td colSpan="6" style={styles.emptyCell}>Aucune modification enregistrée</td>
                    </tr>
                  );
                }

                return validHistory.map(log => (
                  <tr key={log.id} style={styles.trHover}>
                    <td style={styles.td}>
                      {new Date(log.updatedAt).toLocaleString('fr-FR')}
                    </td>
                    <td style={styles.td}>
                      {renderAuditSettingKey(log)}
                    </td>
                    <td style={styles.td}>
                      {renderAuditOldValue(log)}
                    </td>
                    <td style={styles.td}>
                      {renderAuditNewValue(log)}
                    </td>
                    <td style={styles.td}>{log.adminEmail}</td>
                    <td style={styles.td}>
                      <button 
                        onClick={() => handleRestoreClick(log.id, log.settingKey)}
                        style={styles.restoreBtn}
                      >
                        Restaurer
                      </button>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Major pricing warning confirmation Modal */}
      {confirmModal.isOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <ShieldAlert size={28} color="#ef4444" />
              <h3 style={styles.modalTitle}>Modification de Tarif Majeure !</h3>
            </div>
            
            <p style={styles.modalText}>
              Vous êtes sur le point de modifier le paramètre de calcul de prix structurel : 
              <strong style={{ color: '#ffffff' }}> {confirmModal.label}</strong> en 
              <strong style={{ color: '#10b981' }}> {confirmModal.value} €</strong>.
            </p>
            
            <div style={styles.modalAlertBox}>
              <AlertCircle size={18} style={{ flexShrink: 0 }} />
              <span>
                Cette formule tarifaire impactera immédiatement toutes les prochaines estimations et paiements de courses sur l'ensemble de la plateforme, sans redéploiement nécessaire.
              </span>
            </div>
            
            <div style={styles.modalActions}>
              <button 
                onClick={() => setConfirmModal({ isOpen: false, key: null, value: null, label: null })}
                style={styles.cancelBtn}
              >
                Annuler
              </button>
              
              <button 
                onClick={() => {
                  executeSave(confirmModal.key, confirmModal.value);
                  setConfirmModal({ isOpen: false, key: null, value: null, label: null });
                }}
                style={styles.confirmBtn}
              >
                Confirmer l'application
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  gridContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '30px',
    position: 'relative',
  },
  gridEditorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#1e293b',
    padding: '16px 20px',
    borderRadius: '12px',
    border: '1px solid #334155',
    position: 'sticky',
    top: '10px',
    zIndex: 30,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  },
  gridEditorTitle: {
    fontSize: '1.15rem',
    fontWeight: '700',
    color: '#ffffff',
    margin: 0,
  },
  gridEditorDesc: {
    fontSize: '0.85rem',
    color: '#94a3b8',
    margin: '4px 0 0 0',
  },
  modifiedBadgeTop: {
    backgroundColor: 'rgba(16, 185, 129, 0.15)',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    color: '#10b981',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  resetBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#334155',
    color: '#e2e8f0',
    border: '1px solid #475569',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  mainSaveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '0.95rem',
    fontWeight: '700',
    transition: 'all 0.2s',
  },
  searchBarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    backgroundColor: '#1e293b',
    padding: '12px 16px',
    borderRadius: '10px',
    border: '1px solid #334155',
    flexWrap: 'wrap',
  },
  searchInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '8px',
    flex: '1',
    minWidth: '280px',
  },
  gridSearchInput: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#ffffff',
    padding: '8px 12px',
    fontSize: '0.9rem',
    outline: 'none',
  },
  clearSearchBtn: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  quickFilters: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  filterChip: {
    background: '#0f172a',
    border: '1px solid #334155',
    color: '#94a3b8',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterChipActive: {
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    borderColor: '#3b82f6',
    color: '#3b82f6',
    fontWeight: '600',
  },
  emptyGridSearch: {
    padding: '40px',
    textAlign: 'center',
    color: '#94a3b8',
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
    fontSize: '0.95rem',
  },
  gridInputsArea: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
    backgroundColor: '#1e293b',
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid #334155',
  },
  gridInputRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#0f172a',
    padding: '8px 10px',
    borderRadius: '8px',
    border: '1px solid #334155',
    transition: 'all 0.2s',
  },
  gridInputRowModified: {
    backgroundColor: 'rgba(16, 185, 129, 0.08)',
    borderColor: '#10b981',
    boxShadow: '0 0 10px rgba(16, 185, 129, 0.2)',
  },
  kmLabelContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  gridKmLabel: {
    fontSize: '0.85rem',
    fontWeight: '700',
    width: '46px',
  },
  modifiedIndicator: {
    color: '#10b981',
    fontSize: '0.65rem',
  },
  inputEuroWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flex: '1',
  },
  gridInput: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    fontSize: '0.95rem',
    textAlign: 'right',
    padding: '2px',
    outline: 'none',
  },
  gridEuroSymbol: {
    fontSize: '0.85rem',
    color: '#64748b',
    fontWeight: '600',
  },
  floatingActionBar: {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    width: '90%',
    maxWidth: '750px',
    animation: 'slideUp 0.3s ease',
  },
  floatingActionContent: {
    backgroundColor: '#0f172a',
    border: '2px solid #10b981',
    borderRadius: '14px',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.6)',
    gap: '16px',
    flexWrap: 'wrap',
  },
  floatingActionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  pulseDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#10b981',
    boxShadow: '0 0 10px #10b981',
  },
  floatingText: {
    color: '#ffffff',
    fontSize: '0.95rem',
  },
  floatingActionRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  floatingCancelBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#334155',
    color: '#e2e8f0',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  floatingSaveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 22px',
    fontSize: '0.95rem',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
    transition: 'all 0.2s',
  },
  container: {
    padding: '24px',
    color: 'var(--text-primary)',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  loadingContainer: {
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontSize: '1.1rem',
  },
  errorBanner: {
    backgroundColor: '#450a0a',
    border: '1px solid #991b1b',
    borderRadius: '8px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#fca5a5',
    marginBottom: '20px',
  },
  successBanner: {
    backgroundColor: '#064e3b',
    border: '1px solid #065f46',
    borderRadius: '8px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#a7f3d0',
    marginBottom: '20px',
  },
  tabContainer: {
    display: 'flex',
    gap: '12px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '12px',
    marginBottom: '24px',
    overflowX: 'auto',
  },
  tabButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: 'var(--text-muted)',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  tabButtonActive: {
    color: 'var(--blue-primary)',
    borderBottom: '2px solid var(--blue-primary)',
    backgroundColor: 'rgba(59, 130, 246, 0.05)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
  },
  card: {
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'all 0.2s hover',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0,
  },
  cardKey: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    backgroundColor: 'var(--bg-tertiary)',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  cardDesc: {
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
    lineHeight: '1.4',
    marginBottom: '20px',
    flexGrow: 1,
  },
  cardActionArea: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
  },
  input: {
    width: '100%',
    padding: '8px 30px 8px 12px',
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    outline: 'none',
  },
  unitSuffix: {
    position: 'absolute',
    right: '12px',
    color: 'var(--text-muted)',
    fontSize: '0.9rem',
    pointerEvents: 'none',
  },
  saveBtn: {
    backgroundColor: 'var(--blue-primary)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.85rem',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  historySection: {
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '24px',
    marginTop: '40px',
  },
  historyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  historyTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0,
  },
  tablePanel: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
    fontSize: '0.85rem',
  },
  th: {
    borderBottom: '1px solid var(--border-color)',
    padding: '12px 16px',
    color: 'var(--text-muted)',
    fontWeight: '600',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-color)',
    color: 'var(--text-secondary)',
  },
  tr: {
    borderBottom: '1px solid var(--border-color)',
  },
  trHover: {
    borderBottom: '1px solid var(--border-color)',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
    }
  },
  emptyCell: {
    textAlign: 'center',
    padding: '30px',
    color: 'var(--text-muted)',
  },
  restoreBtn: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: 'var(--blue-primary)',
    border: '1px solid rgba(59, 130, 246, 0.2)',
    borderRadius: '4px',
    padding: '4px 10px',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: 'var(--blue-primary)',
      color: '#ffffff',
    }
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  modalCard: {
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '16px',
    padding: '28px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#ffffff',
    margin: 0,
  },
  modalText: {
    fontSize: '0.95rem',
    color: '#94a3b8',
    lineHeight: '1.5',
    marginBottom: '20px',
  },
  modalAlertBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    borderRadius: '8px',
    padding: '12px 16px',
    display: 'flex',
    gap: '12px',
    color: '#f87171',
    fontSize: '0.85rem',
    lineHeight: '1.4',
    marginBottom: '24px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  cancelBtn: {
    backgroundColor: '#334155',
    color: '#e2e8f0',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 18px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  confirmBtn: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 18px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  }
};
